// src/components/Chat/shared/FileUploadModal.tsx
// Modal de upload de arquivos com nomes customizados

import { motion, AnimatePresence, Reorder } from "framer-motion";
import {
  X,
  Upload,
  FileText,
  Image as ImageIcon,
  Music,
  File,
  Trash2,
  GripVertical,
  Send,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef, useCallback } from "react";
import type { FileUploadItem } from "@/types/chat";
import { MAX_FILE_SIZE, getFileCategory, formatFileSize } from "@/types/chat";

interface FileUploadModalProps {
  isOpen: boolean;
  initialFiles: File[];
  onClose: () => void;
  onSend: (files: FileUploadItem[], message: string) => Promise<void>;
}

export function FileUploadModal({
  isOpen,
  initialFiles,
  onClose,
  onSend,
}: FileUploadModalProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Converter arquivos iniciais para FileUploadItem
  useEffect(() => {
    if (initialFiles.length > 0) {
      const items = initialFiles.map((file) => createFileUploadItem(file));
      setFiles(items);
    }
  }, [initialFiles]);

  // Criar item de upload a partir de File
  const createFileUploadItem = (file: File): FileUploadItem => {
    const category = getFileCategory(file.name);
    let preview: string | undefined;

    // Criar preview para imagens
    if (category === "image") {
      preview = URL.createObjectURL(file);
    }

    return {
      file,
      displayName: file.name.replace(/\.[^/.]+$/, ""), // Remove extensão para edição
      preview,
      type: category,
    };
  };

  // Validar arquivo
  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return t("chatFeatures.upload.fileTooLarge");
    }
    return null;
  };

  // Adicionar arquivos
  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      const newErrors: string[] = [];
      const validFiles: FileUploadItem[] = [];

      fileArray.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          newErrors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push(createFileUploadItem(file));
        }
      });

      setErrors(newErrors);
      setFiles((prev) => [...prev, ...validFiles]);
    },
    [t],
  );

  // Handle file input change
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) {
      addFiles(e.dataTransfer.files);
    }
  };

  // Remover arquivo
  const removeFile = (index: number) => {
    setFiles((prev) => {
      const updated = [...prev];
      // Revogar URL de preview se existir
      if (updated[index].preview) {
        URL.revokeObjectURL(updated[index].preview!);
      }
      updated.splice(index, 1);
      return updated;
    });
  };

  // Atualizar nome de exibição
  const updateDisplayName = (index: number, name: string) => {
    setFiles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], displayName: name };
      return updated;
    });
  };

  // Obter ícone do tipo
  const getFileIcon = (type: FileUploadItem["type"]) => {
    switch (type) {
      case "image":
        return <ImageIcon size={20} className="text-green-500" />;
      case "document":
        return <FileText size={20} className="text-blue-500" />;
      case "audio":
        return <Music size={20} className="text-purple-500" />;
      default:
        return <File size={20} className="text-gray-500" />;
    }
  };

  // Obter extensão do arquivo
  const getExtension = (filename: string) => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  // Enviar
  const handleSend = async () => {
    if (files.length === 0 || isSending) return;

    setIsSending(true);
    try {
      await onSend(files, message);
      // Limpar previews
      files.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      setFiles([]);
      setMessage("");
      onClose();
    } catch (error) {
      console.error("Erro ao enviar:", error);
    } finally {
      setIsSending(false);
    }
  };

  // Fechar com ESC
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && !isSending) {
      onClose();
    }
    // Ctrl+Enter para enviar
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !isSending) {
      handleSend();
    }
  };

  // Limpar ao fechar
  const handleClose = () => {
    files.forEach((f) => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setFiles([]);
    setMessage("");
    setErrors([]);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={handleClose}
          onKeyDown={handleKeyDown}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2 text-gray-800">
                <Upload size={20} className="text-blue-500" />
                <h3 className="font-semibold">
                  {t("chatFeatures.upload.title")}
                </h3>
              </div>
              <button
                onClick={handleClose}
                disabled={isSending}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-500 transition-colors disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            {/* Conteúdo */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Drop zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  dragOver
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload
                  size={32}
                  className={`mx-auto mb-2 ${
                    dragOver ? "text-blue-500" : "text-gray-400"
                  }`}
                />
                <p className="text-sm text-gray-600">
                  {t("chatFeatures.upload.dragDrop")}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Máximo: {formatFileSize(MAX_FILE_SIZE)}
                </p>
              </div>

              {/* Erros */}
              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  {errors.map((error, i) => (
                    <p key={i} className="text-sm text-red-600">
                      {error}
                    </p>
                  ))}
                </div>
              )}

              {/* Lista de arquivos */}
              {files.length > 0 && (
                <Reorder.Group
                  axis="y"
                  values={files}
                  onReorder={setFiles}
                  className="space-y-3"
                >
                  {files.map((item, index) => (
                    <Reorder.Item
                      key={item.file.name + index}
                      value={item}
                      className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
                    >
                      <div className="flex items-start gap-3 p-3">
                        {/* Drag handle */}
                        <div className="flex-shrink-0 mt-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                          <GripVertical size={16} />
                        </div>

                        {/* Preview ou ícone */}
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
                          {item.preview ? (
                            <img
                              src={item.preview}
                              alt={item.displayName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            getFileIcon(item.type)
                          )}
                        </div>

                        {/* Info e input de nome */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                            <span className="uppercase font-medium">
                              {getExtension(item.file.name)}
                            </span>
                            <span>•</span>
                            <span>{formatFileSize(item.file.size)}</span>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 mb-0.5 block">
                              {t("chatFeatures.upload.displayName")}
                            </label>
                            <input
                              type="text"
                              value={item.displayName}
                              onChange={(e) =>
                                updateDisplayName(index, e.target.value)
                              }
                              placeholder={t("chatFeatures.upload.placeholder")}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        {/* Botão remover */}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => removeFile(index)}
                          className="flex-shrink-0 p-1.5 rounded-md hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={16} />
                        </motion.button>
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              )}

              {/* Campo de mensagem */}
              {files.length > 0 && (
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">
                    {t("chatFeatures.upload.message")}
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t("chatFeatures.upload.messagePlaceholder")}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 bg-gray-50 border-t border-gray-100">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleClose}
                disabled={isSending}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t("chatFeatures.upload.cancel")}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSend}
                disabled={isSending || files.length === 0}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSending ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                    />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    <span>
                      {t("chatFeatures.upload.send")} ({files.length})
                    </span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default FileUploadModal;
